<template>
  <div id="app" v-if="fetched">
    <div class="selected-date" :class="{ 'not-selected': !selectedDateViewClass }">
      {{ selectedDateView }}
    </div>
    <snack-bar :message="snackbar.message" :active="snackbar.active"></snack-bar>
    <FullCalendar ref="fullCalendar" :options="calendarConfig"></FullCalendar>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import SnackBar from "./components/SnackBar"
import hu from '@fullcalendar/core/locales/hu'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import moment from "moment"
import 'moment-duration-format';
import uuid from "uuidjs";
import isMobile from 'ismobilejs';
import axios from "axios";
import { api_config } from "./config"

export default {
  name: 'App',
  components: {
    FullCalendar,
    SnackBar
  },
  data() {
    return {
      activeServices: [],
      snackbar: {
        message: null,
        active: false
      },
      selectedDate: {
        start: null,
        end: null
      },
      config: {},
      openingHours: {},
      services: {},
      events: {},
      fetched: false,
    };
  },
  mounted() {

    this.selectedCategory = 0;
    this.preload().then(() => {
      this.initServiceCategories();
    });
  },
  computed: {
    /**
     * Calendar config, containing the base configs, and fill up with we got over API.
     */
    calendarConfig() {
      return {
        plugins: [ timeGridPlugin, interactionPlugin ],
        initialView: 'timeGridWeek',
        locale: hu,
        firstDay: 1,
        allDaySlot: false,
        headerToolbar: {
          start: '',
          center: '',
          end: 'today prev,next'
        },
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          omitZeroMinute: false,
          meridiem: false
        },
        dayHeaderFormat: {
          weekday: 'long',
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          omitCommas: true
        },
        expandRows: true,
        height: '100%',
        slotLabelInterval: this.config.interval,
        slotMinTime: this.config.start_time,
        slotMaxTime: this.config.end_time,
        hiddenDays: this.config.hidden_days,
        eventClick: this.handleClick,
        eventContent: function(args) { args.timeText = "" }, // Clear time data, from event diplay.
        eventDidMount: function(args) { args.el.id = args.event.id }, // Add event uuid or id to DOM element id.
        eventMouseEnter: this.handleMouseHover,
        eventMouseLeave: this.handleMouseLeave,
        events: this.events
      }
    },
    /**
     * The interval time we render the calendar, must be in minutes.
     */
    interval() {

         //FIXME
         return parseInt(this.config.intervalInMinutes);         
    },
    /**
     * Calculated maximum months based on config, and formatted with config date_format option.
     */
    maxNextMonth() {
      return moment().add(this.config.max_month, 'months').format(this.config.date_format);
    },
    /**
     * Calculate the sum of services required times, if active services is null, we return the interval data.
     */
    requiredTime() {
      return (this.activeServices.length > 0 ) ? this.activeServices.reduce((last, value) => last + value) : this.interval;
    },
    /**
     * Compute selected date start and end for view.
     */
    selectedDateView() {
      if(this.selectedDate.start && this.selectedDate.end) {
        return this.config.selected_text + ` ${moment(this.selectedDate.start).format(this.config.time_date_format)} - ${moment(this.selectedDate.end).format(this.config.time_format)}`;
      }

      return this.config.not_selected_text;
    },
    /**
     * Compute the selected date style.
     */
    selectedDateViewClass() {
      return this.selectedDate.start !== null && this.selectedDate.end !== null;
    }
  },
  methods: {
    /**
     * Loading data from API.
     */
    loadData() {
      const api_urls = [
        axios.get(`${api_config.url}${api_config.endpoints.opening_hours}`+'/'+this.selectedService),
        axios.get(`${api_config.url}${api_config.endpoints.services}`),
        axios.get(`${api_config.url}${api_config.endpoints.events}`+'/'+this.selectedService+'/'+this.selectedCategory)
      ];

      return axios.all(api_urls).then(axios.spread((...response) => {
        this.openingHours = response[0].data;
        this.services = response[1].data;
        this.events = this.createEventsWithOpenTime(response[2].data);
        this.fetched = true;
      }));
    },
    preload() {
      const api_urls = [
        axios.get(`${api_config.url}${api_config.endpoints.config}`+'/'+this.selectedService),
        axios.get(`${api_config.url}${api_config.endpoints.services}`),
      ];

      return axios.all(api_urls).then(axios.spread((...response) => {
        this.config = response[0].data;
        this.services = response[1].data;
      }));
    },
    loadConfig(serviceID, categoryID) {
        console.log('loadConfig: '+serviceID+' '+categoryID);
        this.activeServices = [];
        this.events = [];

        this.fetched = false;
        const api_urls = [
          axios.get(`${api_config.url}${api_config.endpoints.config}`+'/'+serviceID+'/'+categoryID),
          axios.get(`${api_config.url}${api_config.endpoints.opening_hours}`+'/'+this.selectedService),
          axios.get(`${api_config.url}${api_config.endpoints.services}`),
          axios.get(`${api_config.url}${api_config.endpoints.events}`+'/'+this.selectedService+'/'+categoryID)
        ];
        return axios.all(api_urls).then(axios.spread((...response) => {
          this.config = response[0].data;
          this.openingHours = response[1].data;
          this.services = response[2].data;
          this.events = this.createEventsWithOpenTime(response[3].data);
          this.fetched = true;
        }));

        
    },

    /**
     * Handles click event. 
     * If the required time is more than the interval time we set, then store the prev and next element data. 
     * So we can examine how much time elapses between the two events.
     * 
     * @param info - The clicked element data with el and event data.
     */
    handleClick(info) {
      this.resetSelected();

      if(this.requiredTime > this.interval) {
        const boxesForSelected = (this.requiredTime / this.interval) - 1;
        if(info.el.parentNode.nextElementSibling) {
          let nextElement = info.el.parentNode.nextElementSibling.children[0];
          let prevElement = info.el;

          for(let i = 0; i !== boxesForSelected; i++) {
            const nextElementData = this.findEventById(nextElement.id);
            const prevElementData = this.findEventById(prevElement.id);

            if(moment(nextElementData.start).isSame(prevElementData.end)) {
              info.el.classList.add(this.config.selected_class);
              nextElement.classList.add(this.config.selected_class);

              this.addSelectedDate(info.event.start, nextElementData.end);
            } else {
              this.resetSelected();
              this.activateSnackBar(this.config.error_message);
              break;
            }
            
            prevElement = nextElement;
            if(nextElement.parentNode.nextElementSibling) {
              nextElement = nextElement.parentNode.nextElementSibling.children[0];
            }
          }
        } else {
          this.resetSelected();
          this.activateSnackBar(this.config.error_message);
        }
      } else {
        this.addSelectedDate(info.event.start, info.event.end);
        info.el.classList.add(this.config.selected_class);
      }
    },
    /**
     * Handles mouse enter event. 
     * Mostly same as handleClick function, but here we manipulate class list for proper styled events.
     * 
     * @param info - The hovered element data with el and event data.
     */
    handleMouseHover(info) {
      if(this.requiredTime > this.interval) {
        const boxesForSelected = (this.requiredTime / this.interval) - 1;
        if(info.el.parentNode.nextElementSibling) {
          let nextElement = info.el.parentNode.nextElementSibling.children[0];
          let prevElement = info.el;

          for(let i = 0; i !== boxesForSelected; i++) {
            const nextElementData = this.findEventById(nextElement.id);
            const prevElementData = this.findEventById(prevElement.id);

            if(moment(nextElementData.start).isSame(prevElementData.end) && !prevElement.classList.contains("child-hover-error")) {
              nextElement.classList.add("child-hover");
            } else {
              info.el.classList.add("child-hover-error");
              prevElement.classList.add("child-hover-error");
              nextElement.classList.add("child-hover-error");
            }
            
            prevElement = nextElement;
            if(nextElement.parentNode.nextElementSibling) {
              nextElement = nextElement.parentNode.nextElementSibling.children[0];
            }
          }
        } else {
          info.el.classList.add("child-hover-error");
        }
      }
    },
    /**
     * When mouse leave just delete all unneccessary class.
     */
    handleMouseLeave() {
      document.querySelectorAll(".child-hover").forEach(node => {
        node.classList.remove("child-hover");
      })
      document.querySelectorAll(".child-hover-error").forEach(node => {
        node.classList.remove("child-hover-error");
      })
    },
    /**
     * When mobile device or tablet detected, then shorted dates will appears.
     */
    handleMobileView() {
      if(isMobile(window.navigator).any) {
        document.querySelectorAll(".fc-day").forEach(header => {
          header.firstChild.firstChild.text = moment(header.getAttribute("data-date")).locale('hu').format("MM. DD. ddd")
        })
      }
    },
    /**
     * When we have an error show the snackbar with error massage. 
     * 
     * @param message - The message to write out.
     */
    activateSnackBar(message) {
      this.snackbar.message = message;
      this.snackbar.active = true;
// 
      setTimeout(() => { this.snackbar.active = false }, this.config.error_message_timeout);
    },
    /**
     * When date selected fill the two hidden input with data, and the vue instance data. 
     * 
     * @param start - Start date, formatted with config: time_date_format. 
     * @param end - End data, formatted with config: time_date_format.
     */
    addSelectedDate(start = null, end = null) {
      this.selectedDate.start = start;
      this.selectedDate.end = end;

      document.getElementById(this.config.hidden_input_start_id).value = start ? moment(start).format(this.config.time_date_format) : start;
      document.getElementById(this.config.hidden_input_end_id).value = end ? moment(end).format(this.config.time_date_format) : end;
    },
    /**
     * When got error reset all selected event, and selected date data.
     */
    resetSelected() {
      document.querySelectorAll(`.${this.config.selected_class}`).forEach(node => {
        node.classList.remove(this.config.selected_class);
      })
      this.addSelectedDate();
    },
    /**
     * Find service by id. 
     * 
     * @param id - Service id.
     */
    findService(id) {
      return this.services.find(service => service.id === id);
    },
    /**
     * Find event data within Fullcalendar cached events. 
     * 
     * @param id - Element id, uuid.
     */
    findEventById(id) {
      return this.$refs.fullCalendar.getApi().getEventById(id);
    },
    /**
     * When user interacts with next or prev button, need to check the days of the week for proper render. 
     * 
     * @param weekStart - The start day of the week, must be Sunday.
     */
    getWeekDates(weekStart){
      let days = [];
      const wantedDays = [0,1,2,3,4,5,6];
      
      if(this.config.hidden_days.length > 0) {
        this.config.hidden_days.forEach(hidden => {
          wantedDays.splice(hidden, 1);
        })
      }

      wantedDays.forEach(wanted => {
        days.push(moment(weekStart).add(wanted, 'days').format(this.config.date_format));
      })

      return days;
    },
    /**
     * Fill open events the calendar based on onpening hours.
     * If opening start is before the first event start or the end is, we filled with background event.
     */
    createEventsWithOpenTime(closedEvents) {
      let events = [];

      this.openingHours.forEach(open => {
        const start = (open.start === null) ? moment(this.config.start_time, this.config.time_format) : moment(open.start, this.config.time_format);
        const end = (open.end === null) ? moment(this.config.end_time, this.config.time_format) : moment(open.end, this.config.time_format);
        const diff = end.diff(start);
        const isNonWorkingDay = open.start === null || open.start === null;
        let minutes = moment.duration(diff).format('mm');

        const to = parseInt(minutes/this.interval);

        for(let i = 0; i !== to; i++) {
          const time = start.clone().add(i * this.interval, 'm').format(this.config.time_format);
          const endTime = moment(time, this.config.time_format).add(this.interval, 'm').format(this.config.time_format);
          const isBefore = moment(`${open.date} ${time}`).isBefore(moment());
          
          const closed = closedEvents.find(closedEvent => 
            moment(`${open.date} ${time}`).isBetween(closedEvent.start, closedEvent.end) || moment(`${open.date} ${time}`).isSame(closedEvent.start)
          );

          if(this.config.add_closed_events && closed && !events.find(event => event === closed)) {
            events.push(closed);
          }

          if(!closed && !isNonWorkingDay && !isBefore) {
            events.push({
                id: uuid.generate(),
                title: this.config.open_event_title,
                start: `${open.date} ${time}`,
                end: `${open.date} ${endTime}`,
              }
            );
          }
        }
      })

      return events;
    },
    /**
     * Initialize Button event listeners.
     */
    initButtons() {
    const viewApi = this.$refs.fullCalendar.getApi(); // The view API of FullCalendar.

    // Disable prev button.
    document.querySelector('.fc-prev-button').disabled = true;

    document.querySelector('.fc-prev-button').addEventListener("click", () => {
      document.querySelector('.fc-next-button').disabled = false;

      this.handleMobileView();

      const sunday = moment(viewApi.currentData.viewApi.activeStart).subtract(1, 'day').format(this.config.date_format);
      const weekDays = this.getWeekDates(sunday);
      const isOnWeekDay = weekDays.find(weekDay => 
        weekDay === moment().format(this.config.date_format) || moment(sunday).add(1, "w").format(this.config.date_format) === moment().format(this.config.date_format)
      );
      
      if(isOnWeekDay) {
        document.querySelector('.fc-prev-button').disabled = true;
        document.querySelector('.fc-today-button').disabled = true;
      }
    });

    document.querySelector('.fc-next-button').addEventListener("click", () => {
      document.querySelector('.fc-prev-button').disabled = false;
      document.querySelector('.fc-today-button').disabled = false;

      this.handleMobileView();

      const sunday = moment(viewApi.currentData.viewApi.activeStart).subtract(1, 'day').format(this.config.date_format);
      const weekDays = this.getWeekDates(sunday);
      const isOnWeekDay = weekDays.find(weekDay => weekDay === this.maxNextMonth);

      if(isOnWeekDay) {
        document.querySelector('.fc-next-button').disabled = true;
      }
    });

    document.querySelector('.fc-today-button').addEventListener("click", () => {
      this.handleMobileView();

      document.querySelector('.fc-prev-button').disabled = true;
      document.querySelector('.fc-next-button').disabled = false;
    });
    },
    /**
     * Initialize service checkbox event listener.
     */

   initServiceCategories() {
      const categoriesCheckbox = document.querySelectorAll('.category'); // categories checkboxes.
      for (let i = 0; i < categoriesCheckbox.length; i++) {
        categoriesCheckbox[i].addEventListener("change", () => {          
            this.selectedCategory = categoriesCheckbox[i].value; 
            if ( this.selectedService !== '') {
                this.loadConfig(this.selectedService, categoriesCheckbox[i].value);
            }  
        });
      }          
      this.initServiceCheckboxes();
    },

    initServiceCheckboxes() {
          const servicesCheckbox = document.querySelectorAll(this.config.service_checkbox_class); // Services checkboxes.
          // Add change event listeners on service checkboxes.
          for (let i = 0; i < servicesCheckbox.length; i++) {
            servicesCheckbox[i].addEventListener("change", () => {
                 this.loadConfig(servicesCheckbox[i].value, this.selectedCategory).then(() => {
                      this.resetSelected();
                      this.selectedService = servicesCheckbox[i].value;         
                      const requiredTime = this.config.intervalInMinutes;

                      if(!this.config.multiple_select) {
                        this.activeServices = [];
                      }

                      if(servicesCheckbox[i].checked === true) {
                        this.activeServices.push(requiredTime)
                      } else {
                        const index = this.activeServices.findIndex(active => active === requiredTime)
                        this.activeServices.splice(index, 1);
                      }

                      this.services = [];
                      this.loadData().then(() => {
                        this.initButtons();
                        this.handleMobileView();
                      });
                  });

            });
          }
    },
  }
}
</script>

<style lang="scss">
$event: #007ea7;
$eventHover: #80ced7;
$error: #ff6b6b;
$selected: #0ead69;
$notSelected: #ffba08;
$bgColor: #F1F1F1;
$disabled: #4e4353;
$primary: #FF0000;

#app {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  height: 800px;
  position: relative;
}

.selected-date {
  position: absolute;
  padding: 10px;
  color: white;
  font-weight: 500;
  background: $selected;
  transition: .3s;
  border-radius: 4px;

  &.not-selected {
    background: $notSelected;
  }
}

.child-hover {
  box-shadow: 0px 0px 10px rgba($color: lighten($eventHover, 10), $alpha: 1);
  background: $eventHover !important;
  transform: scale(1.05);
}

.child-hover-error {
  box-shadow: 0px 0px 10px rgba($color: lighten($error, 10), $alpha: 1) !important;
  background: $error !important;
  transform: scale(1.05);
}

.fc .fc-scrollgrid-liquid {
  border-radius: 8px;
  overflow: hidden;
}

.fc-timegrid-event {
  cursor: pointer !important;
  transition: .3s;
  border: none;
  background: $event;
}

.fc-timegrid-event:hover {
  box-shadow: 0px 0px 10px rgba($color: lighten($eventHover, 10), $alpha: 1);
  background: $eventHover;
  transform: scale(1.05);
}

.fc-timegrid-event.selected, .fc-timegrid-event.selected.child-hover {
  background: $selected !important;
}

.fc-timegrid-event.selected:hover, .fc-timegrid-event.selected.child-hover:hover {
  box-shadow: 0px 0px 10px rgba($color: lighten($selected, 10), $alpha: 1) !important;
}

.fc .fc-timegrid-slot {
  background:  rgba($color: $bgColor, $alpha: 0.3);
}

.fc .fc-timegrid-col.fc-day-today {
  background: rgba($color: $bgColor, $alpha: 1.0);
}

.fc-timegrid-slot-label {
  background: white !important;
}

.fc-timegrid-slot-label-cushion {
  font-weight: 300;
}

.fc-event-title {
  font-weight: 500;
  font-size: 18px;
  text-align: center;
  text-transform: uppercase;
}

.fc-event-title-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.fc-direction-ltr .fc-timegrid-col-events {
  width: 100%;
  margin: unset;
}

.fc-scrollgrid {
  box-shadow: 0px 0px 10px rgba($color: #8b8b8b, $alpha: 0.3);
  border: none;
}

.fc-col-header {
  background: #333333;
  color: white;
}

.fc .fc-button-primary {
  background-color: $primary;
  border: none;
  transition: .3s;
}

.fc .fc-button-primary:focus {
  outline: none;
  box-shadow: unset;
}

.fc .fc-button-primary:hover {
  background-color: #ed7669;
}

.fc .fc-button-primary:disabled {
  background-color: $disabled;
}

.fc .fc-button-primary:disabled:hover {
  border-color: unset !important;
}

.fc .fc-button {
  padding: 0px 0.5em;
}

.fc-col-header-cell {
  padding: 10px !important;
}

@media  screen and (max-width: 560px) {
  .fc-event-title {
    width: 2ch;
    overflow: hidden;
    white-space: nowrap;
  }

  .selected-date {
    position: relative;
    margin-bottom: 10px;
  }

  .fc-timegrid-event:hover, .fc-timegrid-event.selected:hover, .fc-timegrid-event.selected.child-hover:hover {
    transform: unset;
    box-shadow: unset;
  }

  .child-hover {
    transform: unset;
    box-shadow: unset;
  }

  .child-hover-error {
    transform: unset;
    box-shadow: unset;
  }
}
</style>